import React from "react";
import { Box, Stack, StackProps, Text, Tooltip } from "@chakra-ui/core";

import { Product, ProductBadge } from "../types";
import { MAX_PRODUCT_BADGES, PRODUCT_BADGE_ICONS } from "../constants";

import { useTranslation } from "~/i18n/hooks";

// How long a tapped badge keeps its label open on small screens
const TAP_LABEL_DURATION = 2500;

// On bigger screens each badge is drawn as a bordered "patch". The bottom
// margin keeps rows apart when the badges wrap.
const PATCH_PROPS = {
  borderColor: "borderStrong",
  borderWidth: 1,
  marginBottom: 1,
  paddingY: 1,
  rounded: "full",
} as const;

interface Props extends StackProps {
  badges: Product["badges"];
  size?: "sm" | "lg";
  // Show the labels on every screen size (e.g. the product detail, where
  // there is room for them even on small screens)
  alwaysShowLabels?: boolean;
}

const ProductBadges: React.FC<Props> = ({
  badges,
  size = "sm",
  alwaysShowLabels = false,
  ...props
}) => {
  const t = useTranslation();
  const [tappedBadge, setTappedBadge] = React.useState<string | null>(null);
  const known = (badges || [])
    .filter((badge) => PRODUCT_BADGE_ICONS[badge as ProductBadge])
    .slice(0, MAX_PRODUCT_BADGES);

  React.useEffect(() => {
    if (!tappedBadge) return;

    const timeout = setTimeout(() => setTappedBadge(null), TAP_LABEL_DURATION);

    return () => clearTimeout(timeout);
  }, [tappedBadge]);

  if (!known.length) return null;

  const fontSize = size === "lg" ? "sm" : "xs";

  // Tapping a badge shows its label instead of opening the product
  function handleTap(event: React.MouseEvent, badge: string) {
    event.stopPropagation();
    setTappedBadge((current) => (current === badge ? null : badge));
  }

  return (
    <>
      {/* Small screens: icons only, label on hover / tap */}
      {!alwaysShowLabels && (
        <Stack
          isInline
          display={{ base: "flex", sm: "none" }}
          flexWrap="wrap"
          spacing={2}
          {...props}
        >
          {known.map((badge) => {
            const label = t(`products.badges.${badge}`);

            return (
              <Tooltip
                key={badge}
                hasArrow
                aria-label={label}
                isOpen={tappedBadge === badge ? true : undefined}
                label={label}
                placement="top"
              >
                <Box
                  aria-label={label}
                  as="span"
                  cursor="pointer"
                  data-test-id={`product-badge-${badge}`}
                  fontSize={size === "lg" ? "sm" : "xs"}
                  lineHeight={1}
                  marginBottom={1}
                  role="img"
                  onClick={(event) => handleTap(event, badge)}
                >
                  {PRODUCT_BADGE_ICONS[badge]}
                </Box>
              </Tooltip>
            );
          })}
        </Stack>
      )}
      {/* Bigger screens: icon with its label next to it */}
      <Stack
        isInline
        display={alwaysShowLabels ? "flex" : { base: "none", sm: "flex" }}
        flexWrap="wrap"
        spacing={1}
        {...props}
      >
        {known.map((badge) => (
          <Stack
            key={badge}
            isInline
            {...PATCH_PROPS}
            alignItems="center"
            data-test-id={`product-badge-${badge}`}
            paddingX={2}
            spacing={1}
          >
            <Text aria-hidden as="span" fontSize={fontSize} lineHeight={1}>
              {PRODUCT_BADGE_ICONS[badge]}
            </Text>
            <Text as="span" color="subtle" fontSize={fontSize} lineHeight={1}>
              {t(`products.badges.${badge}`)}
            </Text>
          </Stack>
        ))}
      </Stack>
    </>
  );
};

export default ProductBadges;
